import classNames from 'classnames';
import { useFormik } from 'formik';
import React, { useCallback } from 'react';
import { Navigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import * as yup from 'yup';
import { APPLY_REGISTER_ID } from '@/modules/common/constants/search-params.ts';
import { onlyNumbers } from '@/modules/common/utils/only-numbers.ts';
import { AUS_ABN, AUS_ACN } from '@/modules/common/utils/regex.ts';
import { useGetBronIdLookUpByAPNQuery } from '@/modules/entity/queries/entity.tsx';
import { CountryNameCodeEnum } from '@/modules/entity/static/countries.ts';
import {
  detectTrustType,
  trustTypeAUSRegulatedOptions,
} from '@/modules/entity/static/trust-types.ts';
import { isValidABN, validateABNorACN } from '@/modules/entity/utils/abn-acn.ts';
import { OnboardingHolderWrapper } from '@/modules/issuer-dashboard/components/holder-create/OnboardingHolderWrapper.tsx';
import { OnboardingStepProps } from '@/modules/issuer-dashboard/components/holder-create/roadsTypes.tsx';
import {
  useIssuerGetInvestorEditByIdQuery,
  useUpdateInvestorEditMutation,
} from '@/modules/issuer-dashboard/queries/holder-create.tsx';
import { issuerDashboardPaths } from '@/modules/issuer-dashboard/routes.tsx';
import { Select, SelectButtons, TextInputField, getSelectOptionByEnum } from 'ui';
import { CountryCodeEnum, CreateEntityRequest, EntityType } from 'api';

export enum UseAbnOrAcn {
  'Enter details manually' = 'no',
  'Use ABN / ACN' = 'yes',
}

interface AbnLookupAndCountryFormik {
  abn_acn?: string;
  country?: CountryNameCodeEnum | CountryCodeEnum | string;
  use_abn_acn?: UseAbnOrAcn;
}

const validationSchema = yup.object({
  abn_acn: yup
    .string()
    .trim()
    .test(
      'abn-or-acn',
      'Incorrect ABN or ACN, example of ABN: 12345678901, example of ACN: 123456789',
      value => {
        if (!value) return true; // Allow nullable (optional) values
        return AUS_ABN.test(value) || AUS_ACN.test(value); // Validate against either ABN or ACN
      },
    )
    .test('is-valid-abn-or-acn', 'Please enter a valid ABN or ACN', value => {
      if (!value) return true; // Allow nullable (optional) values
      const type = validateABNorACN(value);
      if (type === 'ACN') return true; // ACN just needs 9 digits
      if (type === 'ABN') return isValidABN(value);
      return false;
    })
    .nullable()
    .when('use_abn_acn', ([use_abn_acn], schema) => {
      if (use_abn_acn === 'yes') {
        return schema.required('This field is required');
      }
      return schema;
    }),
  use_abn_acn: yup.mixed<UseAbnOrAcn>().oneOf(Object.values(UseAbnOrAcn)),
  country: yup.string().trim().required('This field is required'),
});

export const WhatCountryTab = ({ moveForward }: OnboardingStepProps) => {
  const [params, setParams] = useSearchParams();
  const { state } = useLocation();
  const apply_register_id = params.get(APPLY_REGISTER_ID);
  const { id: entity_id = 0 } = useParams();
  const { data: dataHolder, isLoading } = useIssuerGetInvestorEditByIdQuery(
    entity_id ? +entity_id : -1,
    !entity_id || +entity_id === 0 || isNaN(+entity_id),
  );

  const generateInitialValues = (): AbnLookupAndCountryFormik => {
    const country =
      dataHolder?.data?.new_data?.entity_data?.individual_entity_details?.country ||
      dataHolder?.data?.new_data?.entity_data?.country;
    return {
      country: country ?? 'AUS',
      use_abn_acn:
        (country ?? 'AUS') === 'AUS'
          ? UseAbnOrAcn['Use ABN / ACN']
          : UseAbnOrAcn['Enter details manually'],
    };
  };

  const formik = useFormik<AbnLookupAndCountryFormik>({
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    validationSchema,
    initialValues: generateInitialValues(),
    enableReinitialize: true,
    onSubmit: async (values, { setFieldError }) => {
      if (values.abn_acn) {
        try {
          await mutateAsyncLookupByABN({ abn: values.abn_acn });
          return;
        } catch (e) {
          setFieldError('abn_acn', 'Not found info by this ABN / ACN');
          return;
        }
      }

      // case when registered through transfer
      // if (dataHolder?.data?.id && !dataHolder?.data?.new_data?.entity_data?.type) {
      await handleUpdateEntity({
        country: values?.country as CountryCodeEnum,
        individual_entity_details: {
          country: values?.country as CountryCodeEnum,
        },
      });
      //go to select type
      // moveForward && moveForward();
      // return;
      // }

      //IF NOT abn_acn
      // if (dataHolder?.data?.id) {
      //go to contact details tab
      //   params.set('step', '3');
      //   setParams(params);
      //   navigate(
      //     {
      //       pathname: issuerDashboardPaths.dynamic.updateHolder(String(dataHolder?.data?.id)),
      //       search: params.toString(),
      //     },
      //     { state: { entityId: entity_id, country: values?.country } },
      //   );
      // }
    },
  });

  const { mutateAsync: mutateAsyncLookupByABN, isLoading: isLoadingLookupByABN } =
    useGetBronIdLookUpByAPNQuery({
      onSuccess: async data => {
        let type = data?.data?.entity_type_vestra as EntityType | undefined;

        const entityTypeAbn = data?.data?.entity_type_abn;
        const trustType = detectTrustType(entityTypeAbn);
        const isRegulated = trustTypeAUSRegulatedOptions
          .map(item => item.value)
          .includes(String(trustType));
        // const phone_number = data?.data?.entityNumber;
        const state = data?.data?.address_state;
        const postcode = data?.data?.address_postcode;
        const address = data?.data?.address_text;
        const entity_name = data?.data?.name?.replace(/\./g, '');
        const abn = data?.data?.abn;
        const acn = data?.data?.acn;
        let entity_type_prefix = '';

        if (!type && entityTypeAbn?.toLowerCase()?.includes('company')) {
          type = EntityType.Company;
        }

        switch (type) {
          case EntityType.Trust:
            entity_type_prefix = 'trust_entity_details';
            break;
          case EntityType.Smsf:
            entity_type_prefix = 'smsf_entity_details';
            break;
          case EntityType.Company:
            entity_type_prefix = 'company_entity_details';
            break;
          case EntityType.Individual:
            entity_type_prefix = 'individual_entity_details';
            break;
          case EntityType.Association:
            entity_type_prefix = 'association_entity_details_id';
            break;
          case EntityType.Partnership:
            entity_type_prefix = 'partnership_entity_details';
            break;
        }

        await handleUpdateEntity({
          // ...(apply_register_id ? { apply_register_id: Number(apply_register_id) } : {}),
          apply_register_id: Number(apply_register_id),
          type,
          state,
          postcode,
          // address,
          // entity_name,
          country: (formik.values?.country ?? 'AUS') as CountryCodeEnum,
          // phone_number,
          individual_entity_details: {
            country: (formik.values?.country ?? 'AUS') as CountryCodeEnum,
          },
          [entity_type_prefix]: {
            country: (formik.values?.country ?? 'AUS') as CountryCodeEnum,
            abn,
            acn, //
            type_original: trustType,
            type: ['bareTrust', 'hybridUnitTrust'].includes(String(trustType))
              ? 'otherType'
              : trustType,
            is_regulated: formik.values?.country !== 'AUS' ? undefined : isRegulated,
            registered_office: address,
            company_name: entity_name,
          },
        } as CreateEntityRequest);
      },
    });

  const { mutateAsync: updateInvestor, isLoading: isLoadingUpdate } = useUpdateInvestorEditMutation(
    {
      showNotificationConfirmChanges: false,
      onSuccess: dataHolder => {
        if (dataHolder?.data?.id && !dataHolder?.data?.new_data?.entity_data?.type) {
          //has not type
          //go to select type
          moveForward && moveForward();
          return;
        }

        //has type
        //go to contact details tab
        params.set('step', '2');
        setParams(params, {
          replace: false,
          state: {
            entityId: entity_id,
            country: dataHolder?.data?.new_data?.entity_data?.country,
            from: state?.from,
            from2: state?.from2,
            action: state?.action,
          },
        });
        // navigate(
        //   {
        //     pathname: issuerDashboardPaths.dynamic.updateHolder(String(dataHolder?.data?.id)),
        //     search: params.toString(),
        //   },
        //   {
        //     replace: false,
        //     state: {
        //       entityId: entity_id,
        //       country: dataHolder?.data?.new_data?.entity_data?.country,
        //       from: state?.from,
        //       from2: state?.from2,
        //       action: state?.action,
        //     },
        //   },
        // );
      },
      setErrors: formik.setErrors,
    },
  );

  const handleUpdateEntity = useCallback(
    async (payload: CreateEntityRequest) => {
      if (dataHolder?.data?.id) {
        await updateInvestor({
          id: dataHolder?.data?.id,
          dividend_reinvestment: dataHolder?.data?.new_data?.dividend_reinvestment,
          user_data: dataHolder?.data?.new_data?.user_data,
          entity_data: {
            ...dataHolder?.data?.new_data?.entity_data, // ToDo: After changed type need delete all prev fields
            ...payload,
          },
        });
      }
    },
    [dataHolder?.data, updateInvestor],
  );

  const handleChangeSelect = (field: string, value: string | boolean | UseAbnOrAcn) => {
    formik.setFieldValue(field, value);
    formik.setFieldError(field, '');
    if (field === 'country' && value !== 'AUS') {
      handleChangeSelect('use_abn_acn', 'no');
    }
    if (field === 'country' && value === 'AUS') {
      handleChangeSelect('use_abn_acn', 'yes');
    }
    if (field === 'use_abn_acn' && value === 'no') {
      formik.setFieldValue('abn_acn', '');
      formik.setFieldError('abn_acn', '');
    }
  };

  const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const field = e.target.name;
    const value = onlyNumbers(e.target.value);
    formik.setFieldValue(field, value);
    formik.setFieldError(field, '');
  };

  if (
    !!dataHolder?.data?.new_data?.entity_data?.country &&
    !!dataHolder?.data?.new_data?.entity_data?.type
  ) {
    // if (!dataHolder?.data?.new_data?.entity_data?.type) {
    //   params.set('step', '2');
    // } else {
    //   params.set('step', '3');
    // }
    params.set('step', '3');

    return (
      <Navigate
        to={{
          pathname: issuerDashboardPaths.dynamic.updateHolder(String(dataHolder?.data?.id)),
          search: params.toString(),
        }}
        replace={true}
        state={{
          entityId: entity_id,
          country: dataHolder?.data?.new_data?.entity_data?.country,
          from: state?.from,
          from2: state?.from2,
          action: state?.action,
        }}
      />
    );
  }

  return (
    <OnboardingHolderWrapper
      title="What country is your entity based?"
      onClickContinue={formik.handleSubmit}
      formik={formik}
      disableNext={isLoading || isLoadingUpdate || isLoadingLookupByABN}
      isLoading={isLoading || isLoadingUpdate || isLoadingLookupByABN}
      showBackButton={false}
    >
      <div className="w-full sm:h-full flex flex-col justify-center items-center gap-10">
        <div className="flex flex-col gap-8 w-full">
          <Select
            isClearable
            label="Country"
            options={getSelectOptionByEnum<CountryNameCodeEnum>(CountryNameCodeEnum, true)}
            value={formik.values.country as unknown as CountryNameCodeEnum}
            onChange={value => handleChangeSelect('country', value || '')}
            error={formik.errors.country}
          />

          {formik.values.country === 'AUS' && (
            <>
              <div className="flex flex-col">
                <span
                  className={classNames(
                    'text-gray-900 font-[Inter] text-[10px] leading-[18px] font-medium opacity-64 uppercase mb-2',
                  )}
                >
                  Pull data automatically using your ABN or enter your full business details
                  manually?
                </span>
                <SelectButtons
                  options={getSelectOptionByEnum<UseAbnOrAcn>(UseAbnOrAcn, true, false)}
                  selected={getSelectOptionByEnum<UseAbnOrAcn>(UseAbnOrAcn).find(
                    ({ value }) => value === formik.values.use_abn_acn,
                  )}
                  disabled={
                    !!dataHolder?.data?.id && !!dataHolder?.data?.new_data?.entity_data?.type
                  }
                  capitalize={false}
                  onChange={value => {
                    handleChangeSelect('use_abn_acn', value);
                  }}
                />
              </div>

              {formik.values.use_abn_acn === 'yes' && (
                <TextInputField label={'ABN / ACN'} name="abn_acn" onChange={handleChangeInput} />
              )}
            </>
          )}
        </div>
      </div>
    </OnboardingHolderWrapper>
  );
};
